import { useState, useEffect, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useScannerContext } from "@/contexts/ScannerContext";
import { useNFCReader } from "@/hooks/useNFCReader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { CompactStepper } from "@/components/ui/Stepper";
import { api } from "@/lib/apiClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { addPendingOperation } from "@/lib/offlineStorage";
import { BookOpenCheck, Scissors, Layers, Route, Box, Sparkles, Save, FileText, Nfc, ScanLine, Wrench, Building2 } from "lucide-react";
import { z } from "zod";
import { format } from "date-fns";
import { FreezerPackageSearch } from "./FreezerPackageSearch";
import { ProcessAssignments } from "./ProcessAssignments";
import { MoldSelector } from "./MoldSelector";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";

// Mapa de códigos para tipos de tareas internas
const INTERNAL_TASK_CODES: Record<string, string> = {
  training: 'CAP',
  report: 'INF',
  audit: 'AUD',
  meeting: 'REU',
  documentation: 'DOC',
  cleaning: 'LIM',
  maintenance: 'MAN',
  organization: 'ORG',
  inventory: 'INV',
  equipment_check: 'EQP',
};

// Schema de validación base
const workOrderSchema = z.object({
  productCode: z.string()
    .max(50, "El código no puede exceder 50 caracteres")
    .optional(),
  productName: z.string()
    .min(1, "El nombre del producto es requerido")
    .max(200, "El nombre no puede exceder 200 caracteres"),
  customerId: z.string().optional(),
  projectId: z.string().optional(),
  customProjectName: z.string().max(255).optional(),
  partId: z.string()
    .min(1, "El ID de pieza es requerido")
    .max(50, "El ID de pieza no puede exceder 50 caracteres")
    .regex(/^[A-Z0-9\-_]+$/i, "Solo letras, números, guiones y guiones bajos"),
  operatorId: z.string().optional(),
  nfcTagUid: z.string().optional(),
  quantity: z.number()
    .min(1, "La cantidad mínima es 1")
    .max(999999, "La cantidad máxima es 999,999"),
  priority: z.enum(["low", "normal", "high", "urgent"]),
  startDate: z.string().min(1, "La fecha de inicio es requerida"),
});

interface CreateWorkOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateWorkOrderDialog({
  open,
  onOpenChange,
}: CreateWorkOrderDialogProps) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { nfcBridge } = useScannerContext();
  const { isSupported: isMobileNfcSupported, scanTagUid, isScanning: isMobileScanning } = useNFCReader();
  const queryClient = useQueryClient();
  const isOnline = useOnlineStatus();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [detectedNewTag, setDetectedNewTag] = useState<string | null>(null);

  // Validacion on-blur
  const validateField = (fieldName: string, value: string) => {
    let error = '';
    switch (fieldName) {
      case 'productName':
        if (!value.trim()) error = 'El nombre del producto es requerido';
        else if (value.length > 200) error = 'Maximo 200 caracteres';
        break;
      case 'partId':
        if (!value.trim()) error = 'El ID de pieza es requerido';
        else if (!/^[A-Z0-9\-_]+$/i.test(value)) error = 'Solo letras, numeros, guiones';
        else if (value.length > 50) error = 'Maximo 50 caracteres';
        break;
      case 'quantity':
        const qty = Number(value);
        if (isNaN(qty) || qty < 1) error = 'Cantidad minima es 1';
        else if (qty > 999999) error = 'Cantidad maxima es 999,999';
        break;
    }
    setErrors(prev => ({ ...prev, [fieldName]: error }));
    return !error;
  };

  const handleFieldBlur = (fieldName: string) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
    validateField(fieldName, formData[fieldName as keyof typeof formData]?.toString() || '');
  };


  const [scanningMobile, setScanningMobile] = useState(false);
  const [lastUsbReadTimestamp, setLastUsbReadTimestamp] = useState<number>(0);

  const [formData, setFormData] = useState({
    productModelId: "",
    productCode: "",
    productName: "",
    customerId: "",
    projectId: "",
    customProjectName: "",
    partId: "",
    operatorId: "",
    nfcTagUid: "",
    quantity: 1,
    priority: "normal",
    startDate: format(new Date(), "yyyy-MM-dd"),
    // Workflow fields
    workflowType: "plotter_first" as "plotter_only" | "plotter_first" | "direct_lamination" | "standard" | "internal",
    orderType: "normal" as "plotter" | "normal" | "design_cnc" | "mold",
    linkedPlotterCutId: "",
    moldId: "",
    // Internal task fields
    internalTaskType: "" as string,
  });
  const [useManualProject, setUseManualProject] = useState(false);
  const [processAssignments, setProcessAssignments] = useState<Array<{ processId: string; operatorId: string; itemId?: string }>>([]);
  const [assignmentValidation, setAssignmentValidation] = useState<{ isValid: boolean; missingRequired: string[] }>({ isValid: true, missingRequired: [] });

  // Generar items temporales para vista "Por Pieza" basado en quantity
  const tempWorkOrderItems = useMemo(() => {
    return Array.from({ length: formData.quantity }, (_, i) => ({
      id: `temp-item-${i + 1}`,
      item_number: i + 1,
      part_label: null,
    }));
  }, [formData.quantity]);

  const [selectedFreezerPackage, setSelectedFreezerPackage] = useState<any>(null);
  const [selectedMold, setSelectedMold] = useState<any>(null);

  // Cargar clientes
  const { data: customers} = useQuery({
    queryKey: ["customers-select"],
    queryFn: async () => {
      const { data, error } = await api
        .from("customers")
        .select("id, company_name, codificacion")
        .eq("is_active", true)
        .order("company_name", { ascending: true });
      return data;
    },
  });

  // Cargar proyectos filtrados por cliente
  const { data: projects } = useQuery({
    queryKey: ["projects-by-customer", formData.customerId],
    queryFn: async () => {
      if (!formData.customerId) return [];
      const { data, error } = await api
        .from("projects")
        .select("id, name, reference_code")
        .eq("customer_id", formData.customerId)
        .eq("is_active", true)
        .order("name", { ascending: true });
      return data || [];
    },
    enabled: !!formData.customerId,
  });

  // Cargar productos filtrados por cliente
  const { data: products } = useQuery({
    queryKey: ["products-by-customer", formData.customerId],
    queryFn: async () => {
      if (!formData.customerId) return [];
      const { data, error } = await api
        .from("product_models")
        .select("id, name, product_number, reference")
        .eq("customer_id", formData.customerId)
        .eq("is_active", true)
        .order("name", { ascending: true });
      return data || [];
    },
    enabled: !!formData.customerId,
  });

  const { data: operators } = useQuery({
    queryKey: ["operators-select"],
    queryFn: async () => {
      const { data, error } = await api
        .from("profiles")
        .select("id, full_name, employee_id")
        .eq("is_active", true)
        .order("full_name", { ascending: true });
      return data;
    },
  });

  // Preview del número de orden (se actualiza al seleccionar cliente/proyecto)
  const previewOrderNumber = useMemo(() => {
    const isInternal = formData.workflowType === 'internal';
    const selectedCustomer = customers?.find((c: any) => c.id === formData.customerId);
    const selectedProject = projects?.find((p: any) => p.id === formData.projectId);
    const selectedOperator = operators?.find((o: any) => o.id === formData.operatorId);

    if (isInternal) {
      const taskCode = formData.internalTaskType ? INTERNAL_TASK_CODES[formData.internalTaskType] || '???' : '???';
      const employeeCode = selectedOperator?.employee_id || '???';
      return `TA-${taskCode}-${employeeCode}-?????`;
    } else {
      const customerCode = selectedCustomer?.codificacion || '???';
      const projectCode = useManualProject
        ? (formData.customProjectName?.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10) || '???')
        : (selectedProject?.reference_code || '???');
      return `OT-${customerCode}-${projectCode}-?????`;
    }
  }, [formData.customerId, formData.projectId, formData.workflowType, formData.operatorId, formData.customProjectName, formData.internalTaskType, useManualProject, customers, projects, operators]);

  const { data: availableTags } = useQuery({
    queryKey: ["nfc-tags-available"],
    queryFn: async () => {
      const { data, error } = await api
        .from("nfc_tags")
        .select("tag_uid, tag_label")
        .eq("status", "active")
        .is("work_order_id", null)
        .is("process_id", null)
        .order("created_at", { ascending: false });
      return data;
    },
    enabled: open,
  });

  // Detectar lectura NFC desde lector USB (bridge)
  useEffect(() => {
    if (open && nfcBridge.lastRead && nfcBridge.lastRead.timestamp > lastUsbReadTimestamp) {
      const uid = nfcBridge.lastRead.uid;
      setLastUsbReadTimestamp(nfcBridge.lastRead.timestamp);
      const exists = (availableTags || []).some((t: any) => t.tag_uid === uid);
      if (exists) {
        setFormData((prev) => ({ ...prev, nfcTagUid: uid }));
        setDetectedNewTag(null);
        toast.success(`Etiqueta NFC detectada (USB): ${uid}`);
      } else {
        setDetectedNewTag(uid);
        setFormData((prev) => ({ ...prev, nfcTagUid: uid }));
        toast.success(`Nuevo chip NFC detectado (USB): ${uid} - Se registrará al guardar`);
      }
    }
  }, [open, nfcBridge.lastRead, lastUsbReadTimestamp, availableTags]);

  // Auto-defaults: buscar plantilla o inferir de OT anteriores
  const { data: suggestedDefaults, isLoading: loadingDefaults } = useQuery({
    queryKey: ['wo-defaults', formData.customerId, formData.projectId],
    queryFn: async () => {
      if (!formData.customerId) return null;

      // 1. Buscar plantilla guardada
      const { data: templates } = await api
        .from('work_order_templates')
        .select('*')
        .eq('customer_id', formData.customerId)
        .eq('is_active', true)
        .order('usage_count', { ascending: false })
        .limit(1);

      if (templates && templates.length > 0) {
        // Si hay proyecto seleccionado, buscar plantilla específica
        if (formData.projectId) {
          const projectTemplate = templates.find((t: any) => t.project_id === formData.projectId);
          if (projectTemplate) {
            return { type: 'template', data: projectTemplate };
          }
        }
        // Si no hay proyecto específico, usar la plantilla general del cliente
        return { type: 'template', data: templates[0] };
      }

      // 2. Inferir de últimas OT similares
      let query = api
        .from('work_orders')
        .select('workflow_type, mold_id, priority')
        .eq('customer_id', formData.customerId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (formData.projectId) {
        query = query.eq('project_id', formData.projectId);
      }

      const { data: recentWOs } = await query;

      if (recentWOs && recentWOs.length > 0) {
        // Encontrar el valor más común de workflow_type
        const workflowCounts: Record<string, number> = {};
        const priorityCounts: Record<number, number> = {};
        let mostCommonMold = null;
        let mostCommonPlybook = null;

        recentWOs.forEach((wo: any) => {
          if (wo.workflow_type) {
            workflowCounts[wo.workflow_type] = (workflowCounts[wo.workflow_type] || 0) + 1;
          }
          if (wo.priority) {
            priorityCounts[wo.priority] = (priorityCounts[wo.priority] || 0) + 1;
          }
          if (wo.mold_id && !mostCommonMold) mostCommonMold = wo.mold_id;
          if (wo.plybook_id && !mostCommonPlybook) mostCommonPlybook = wo.plybook_id;
        });

        const mostCommonWorkflow = Object.entries(workflowCounts)
          .sort((a, b) => b[1] - a[1])[0]?.[0] || 'plotter_first';

        const mostCommonPriority = Object.entries(priorityCounts)
          .sort((a, b) => b[1] - a[1])[0]?.[0];

        // Convertir priority numérico a string
        const priorityMap: Record<number, string> = { 1: 'low', 2: 'normal', 3: 'high', 4: 'urgent' };

        return {
          type: 'inferred',
          data: {
            workflow_type: mostCommonWorkflow,
            default_mold_id: mostCommonMold,
            default_priority: priorityMap[Number(mostCommonPriority)] || 'normal',
          },
          count: recentWOs.length,
        };
      }

      return null;
    },
    enabled: !!formData.customerId && open,
  });

  // Estado para controlar si ya se aplicaron los defaults
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  // Aplicar defaults cuando estén disponibles
  useEffect(() => {
    if (suggestedDefaults && !defaultsApplied && open) {
      const defaults = suggestedDefaults.data;

      setFormData(prev => ({
        ...prev,
        workflowType: defaults.workflow_type || prev.workflowType,
        moldId: defaults.default_mold_id || prev.moldId,
        priority: defaults.default_priority || prev.priority,
        plybookId: defaults.plybook_id || prev.plybookId,
      }));

      setDefaultsApplied(true);
    }
  }, [suggestedDefaults, defaultsApplied, open]);

  // Reset defaultsApplied when customer/project changes
  useEffect(() => {
    setDefaultsApplied(false);
  }, [formData.customerId, formData.projectId]);

// Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setErrors({});
      setTouched({});
    }
  }, [open]);
  // Estado para guardar como plantilla
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  
  // Fetch saved templates
  const { data: savedTemplates } = useQuery({
    queryKey: ['wo-templates-list'],
    queryFn: async () => {
      const { data } = await api
        .from('work_order_templates')
        .select('*, customer:customers(company_name), project:projects(name)')
        .eq('is_active', true)
        .order('usage_count', { ascending: false });
      return data || [];
    },
  });

  // Verificar si el formulario es valido para habilitar submit
  const isFormValid = formData.productName.trim() && 
                      formData.partId.trim() && 
                      formData.quantity >= 1 &&
                      !Object.values(errors).some(e => e);

  // Calcular paso actual para el stepper
  const getCurrentStep = () => {
    if (!formData.productName.trim()) return 0;
    if (!formData.partId.trim()) return 1;
    if (!formData.workflowType) return 2;
    return 3;
  };
  const currentStep = getCurrentStep();
  const stepLabels = ['Producto', 'Identificacion', 'Proceso', 'Confirmar'];
  const [templateName, setTemplateName] = useState('');

  // Función para guardar plantilla
  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Ingresa un nombre para la plantilla');
      return;
    }

    try {
      const { error } = await api.from('work_order_templates').insert({
        name: templateName.trim(),
        customer_id: formData.customerId || null,
        project_id: formData.projectId || null,
        workflow_type: formData.workflowType,
        order_type: formData.orderType,
        default_mold_id: formData.moldId || null,
        default_priority: formData.priority,
        default_quantity: formData.quantity,
        created_by: user?.id,
      });

      toast.success('Plantilla guardada exitosamente');
      setShowSaveTemplate(false);
      setTemplateName('');
      queryClient.invalidateQueries({ queryKey: ['wo-defaults'] });
    } catch (error: any) {
      toast.error(error.message || 'Error al guardar plantilla');
    }
  };

  // Función para cargar plantilla guardada
  const loadTemplate = async (template: any) => {
    setFormData(prev => ({
      ...prev,
      customerId: template.customer_id || prev.customerId,
      projectId: template.project_id || prev.projectId,
      workflowType: template.workflow_type || prev.workflowType,
      orderType: template.order_type || prev.orderType,
      moldId: template.default_mold_id || prev.moldId,
      priority: template.default_priority || prev.priority,
      quantity: template.default_quantity || prev.quantity,
    }));
    // Actualizar contador de uso
    await api.from('work_order_templates')
      .update({ usage_count: (template.usage_count || 0) + 1, last_used_at: new Date().toISOString() })
      .eq('id', template.id);
    setShowTemplateSelector(false);
    toast.success();
    queryClient.invalidateQueries({ queryKey: ['wo-templates-list'] });
  };

  // Generar número de orden automáticamente
  // OT: OT-(cliente)-(proyecto)-(consecutivo_universal)
  // TA: TA-(tipo_tarea)-(empleado)-(consecutivo_universal)
  const generateOrderNumber = async () => {
    const isInternal = formData.workflowType === 'internal';
    const selectedCustomer = customers?.find((c: any) => c.id === formData.customerId);
    const selectedProject = projects?.find((p: any) => p.id === formData.projectId);
    const selectedOperator = operators?.find((o: any) => o.id === formData.operatorId);

    try {
      if (isInternal) {
        // Tarea interna: TA-(tipo_tarea)-(empleado)-(consecutivo)
        const taskCode = formData.internalTaskType ? INTERNAL_TASK_CODES[formData.internalTaskType] || 'INT' : 'INT';
        const employeeCode = selectedOperator?.employee_id || 'EMP';

        const { data, error } = await api.rpc('generate_internal_task_number', {
          p_internal_code: taskCode,
          p_employee_code: employeeCode,
        });

        return data as string;
      } else {
        // Orden de trabajo: OT-(cliente)-(proyecto)-(consecutivo)
        const customerCode = selectedCustomer?.codificacion || 'GEN';
        const projectCode = useManualProject
          ? (formData.customProjectName?.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10) || 'MAN')
          : (selectedProject?.reference_code || 'GEN');

        const { data, error } = await api.rpc('generate_work_order_number', {
          p_customer_code: customerCode,
          p_project_code: projectCode,
        });

        return data as string;
      }
    } catch (error) {
      console.error('Error generating order number:', error);
      // Fallback: generar localmente si el RPC falla
      const timestamp = Date.now().toString(36).toUpperCase();
      if (isInternal) {
        const taskCode = formData.internalTaskType ? INTERNAL_TASK_CODES[formData.internalTaskType] || 'INT' : 'INT';
        const employeeCode = selectedOperator?.employee_id || 'EMP';
        return `TA-${taskCode}-${employeeCode}-${timestamp}`;
      } else {
        const customerCode = selectedCustomer?.codificacion || 'GEN';
        const projectCode = selectedProject?.reference_code || 'GEN';
        return `OT-${customerCode}-${projectCode}-${timestamp}`;
      }
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setErrors({});
    setTouched({});

    // Validación con Zod
    const validationResult = workOrderSchema.safeParse(formData);

    if (!validationResult.success) {
      const fieldErrors: Record<string, string> = {};
      validationResult.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      toast.error("Por favor corrige los errores del formulario");
      return;
    }

    // Validate customer is required for non-internal tasks
    if (formData.workflowType !== 'internal' && !formData.customerId) {
      toast.error("Selecciona un cliente para la orden de trabajo");
      setLoading(false);
      return;
    }

    // Validate that direct_lamination requires a package and mold
    if (formData.workflowType === "direct_lamination") {
      if (!formData.linkedPlotterCutId) {
        toast.error("Debes seleccionar un paquete del congelador para laminación directa");
        return;
      }
      if (!formData.moldId) {
        toast.error("Debes seleccionar un molde para laminación directa");
        return;
      }
    }

    // Validate required process assignments (skip for internal tasks)
    if (formData.workflowType !== 'internal' && !assignmentValidation.isValid && assignmentValidation.missingRequired.length > 0) {
      toast.error("Faltan asignaciones obligatorias", {
        description: `Debes asignar operarios a: ${assignmentValidation.missingRequired.join(", ")}`,
      });
      return;
    }

    // Validate internal task type
    if (formData.workflowType === 'internal' && !formData.internalTaskType) {
      toast.error("Selecciona el tipo de tarea interna");
      return;
    }

    setLoading(true);
    try {
      // Generar número de orden con consecutivo real
      const orderNumber = await generateOrderNumber();
      if (!orderNumber) {
        throw new Error("No se pudo generar el número de orden");
      }
      // Convertir prioridad string a número para la base de datos
      const priorityMap: Record<string, number> = {
        low: 1,
        normal: 2,
        high: 3,
        urgent: 4,
      };

      // Obtener el nombre del cliente si se seleccionó uno
      const selectedCustomer = customers?.find((c: any) => c.id === formData.customerId);

      // Find starting process based on workflow type
      let startingProcessId = null;
      if (formData.workflowType === 'plotter_first') {
        // Find material_cutting process
        const { data: cuttingProcess } = await api
          .from('manufacturing_processes')
          .select('id')
          .eq('code', 'material_cutting')
          .single();
        startingProcessId = cuttingProcess?.id || null;
      } else if (formData.workflowType === 'direct_lamination') {
        // Find material_layup process
        const { data: layupProcess } = await api
          .from('manufacturing_processes')
          .select('id')
          .eq('code', 'material_layup')
          .single();
        startingProcessId = layupProcess?.id || null;
      }

      const workOrderData = {
        order_number: orderNumber,
        product_code: formData.productCode || null,
        product_name: formData.productName,
        customer_id: selectedCustomer?.id || null,
        customer: selectedCustomer?.company_name || null,
        project_id: formData.projectId || null,
        part_id: formData.partId || null,
        custom_project_name: useManualProject ? formData.customProjectName : null,
        assigned_operator: formData.operatorId || null,
        quantity: formData.quantity,
        priority: priorityMap[formData.priority] || 2,
        created_by: user.id,
        status: "pending",
        // Fecha de inicio: la OT entra programada en el planificador de
        // producción para ese día (misma semántica que ProductionPlanner)
        scheduled_date: formData.startDate,
        planning_status: "scheduled",
        // Workflow fields
        workflow_type: formData.workflowType,
        order_type: formData.orderType,
        starting_process_id: startingProcessId,
        linked_plotter_cut_id: formData.linkedPlotterCutId || null,
        // Las OTs de solo corte no llevan molde (se asigna en la OT de laminado)
        mold_id: (formData.workflowType === 'plotter_first' || formData.workflowType === 'plotter_only')
          ? null
          : (formData.moldId || null),
        // Internal task fields
        internal_task_type: formData.workflowType === 'internal' ? formData.internalTaskType : null,
      };

      if (isOnline) {
        const { data: createdOrders, error } = await api
          .from("work_orders")
          .insert(workOrderData)
          .select("id, order_number");
        const createdOrder = createdOrders?.[0];

        if (createdOrder) {
          // Assign NFC tag if selected (or register + assign if it's a new chip)
          if (formData.nfcTagUid) {
            const isNewChip = detectedNewTag === formData.nfcTagUid;
            if (isNewChip) {
              const { error: insertError } = await api
                .from("nfc_tags")
                .insert({
                  tag_uid: formData.nfcTagUid,
                  tag_label: `OT-${createdOrder.order_number}`,
                  work_order_id: createdOrder.id,
                  tag_type: "work_order",
                  status: "active",
                  assigned_at: new Date().toISOString(),
                  assigned_by: user.id,
                  created_by: user.id,
                });
              if (insertError && !insertError.message?.includes("duplicate")) throw insertError;
            } else {
              const { error: tagError } = await api
                .from("nfc_tags")
                .update({
                  work_order_id: createdOrder.id,
                  process_id: null,
                  tag_type: "work_order",
                  assigned_at: new Date().toISOString(),
                  assigned_by: user.id,
                })
                .eq("tag_uid", formData.nfcTagUid);
              if (tagError) throw tagError;
            }
          }

          // Save process assignments if any
          if (processAssignments.length > 0) {
            const assignmentsData = processAssignments
              .filter(a => a.operatorId)
              .map((a, index) => ({
                work_order_id: createdOrder.id,
                process_id: a.processId,
                assigned_operator_id: a.operatorId,
                sequence_order: index,
                assigned_by: user.id,
                status: 'pending',
              }));

            if (assignmentsData.length > 0) {
              const { error: assignError } = await api
                .from("work_order_process_assignments")
                .insert(assignmentsData);
              if (assignError) {
                console.error('Error saving process assignments:', assignError);
              }
            }
          }

          // Update linked plotter cut if direct lamination
          if (formData.linkedPlotterCutId) {
            const { error: linkError } = await api
              .from("plotter_cuts")
              .update({
                assigned_work_order_id: createdOrder.id,
                // 'assigned' no existe en el CHECK de plotter_cuts.status y el
                // update fallaba en silencio dejando el paquete sin vincular
                status: 'in_use',
              })
              .eq("id", formData.linkedPlotterCutId);
            if (linkError) {
              console.error('Error linking plotter cut:', linkError);
              toast.error('El paquete no se pudo vincular a la OT', {
                description: linkError.message,
              });
            }
          }
        }

        toast.success("Orden de trabajo creada exitosamente");
      } else {
        addPendingOperation({
          type: "work_order",
          action: "create",
          data: workOrderData,
        });
        toast.success("Orden guardada localmente. Se sincronizará al reconectar.");
      }

      queryClient.invalidateQueries({ queryKey: ["work-orders"] });
      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || "Error al crear la orden");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      productModelId: "",
      productCode: "",
      productName: "",
      customerId: "",
      projectId: "",
      customProjectName: "",
      partId: "",
      operatorId: "",
      nfcTagUid: "",
        quantity: 1,
      priority: "normal",
      startDate: format(new Date(), "yyyy-MM-dd"),
      workflowType: "plotter_first",
      orderType: "normal",
      linkedPlotterCutId: "",
      moldId: "",
      internalTaskType: "",
    });
    setUseManualProject(false);
    setProcessAssignments([]);
    setSelectedFreezerPackage(null);
    setSelectedMold(null);
    setErrors({});
    setDefaultsApplied(false);
    setShowSaveTemplate(false);
    setTemplateName('');
    setDetectedNewTag(null);
    setLastUsbReadTimestamp(0);
    setAssignmentValidation({ isValid: true, missingRequired: [] });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t.workOrders.createOrder}</DialogTitle>
        </DialogHeader>

        {/* Progress Stepper */}
        <CompactStepper steps={stepLabels} currentStep={currentStep} className="mb-2" />

        {/* Banner de defaults sugeridos */}
        {suggestedDefaults && formData.customerId && (
          <Alert className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
            <Sparkles className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-sm text-blue-800 dark:text-blue-300">
              {suggestedDefaults.type === 'template' ? (
                <>
                  <span className="font-medium">Plantilla aplicada:</span> {suggestedDefaults.data.name}
                </>
              ) : (
                <>
                  <span className="font-medium">Valores sugeridos</span> basados en {suggestedDefaults.count} OT anteriores de este cliente
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Preview del Número de Orden */}
          <div className="space-y-2">
            <Label>Número de Orden (auto-generado)</Label>
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-md border border-dashed">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className={`font-mono text-lg ${formData.customerId ? 'text-foreground' : 'text-muted-foreground'}`}>
                {previewOrderNumber}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              El consecutivo final se asignará al crear la orden
            </p>
          </div>

          {/* Cliente */}
          <div className="space-y-2">
            <Label>Cliente *</Label>
            <Select
              value={formData.customerId || "none"}
              onValueChange={(value) => {
                setFormData({
                  ...formData,
                  customerId: value === "none" ? "" : value,
                  projectId: "", // Reset project when customer changes
                  customProjectName: "",
                });
                setUseManualProject(false);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin cliente</SelectItem>
                {customers?.filter((customer: any) => customer.id).map((customer: any) => (
                  <SelectItem key={customer.id} value={customer.id}>
                    {customer.company_name} {customer.codificacion && `(${customer.codificacion})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Proyecto - Solo visible si hay cliente seleccionado */}
          {formData.customerId && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Proyecto</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs h-6"
                  onClick={() => {
                    setUseManualProject(!useManualProject);
                    setFormData({ ...formData, projectId: "", customProjectName: "" });
                  }}
                >
                  {useManualProject ? "Seleccionar de lista" : "Ingresar manualmente"}
                </Button>
              </div>

              {useManualProject ? (
                <Input
                  value={formData.customProjectName}
                  onChange={(e) =>
                    setFormData({ ...formData, customProjectName: e.target.value })
                  }
                  placeholder="Nombre del proyecto"
                />
              ) : (
                <Select
                  value={formData.projectId || "none"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, projectId: value === "none" ? "" : value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar proyecto (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin proyecto</SelectItem>
                    {projects?.filter((project: any) => project.id).map((project: any) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name} {project.reference_code && `(${project.reference_code})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Producto - Solo visible si hay cliente seleccionado */}
          {formData.customerId && (
            <div className="space-y-2">
              <Label>Producto *</Label>
              <Select
                value={formData.productModelId || "none"}
                onValueChange={(value) => {
                  if (value === "none") {
                    setFormData({ ...formData, productModelId: "", productCode: "", productName: "" });
                  } else {
                    const product = products?.find((p: any) => p.id === value);
                    setFormData({
                      ...formData,
                      productModelId: value,
                      productCode: product ? `PROD-${String(product.product_number).padStart(4, "0")}` : "",
                      productName: product?.name || "",
                    });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar producto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin producto</SelectItem>
                  {products?.filter((product: any) => product.id).map((product: any) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} {product.reference && `(${product.reference})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.productModelId && (
                <p className="text-xs text-muted-foreground">
                  Código: {formData.productCode}
                </p>
              )}
            </div>
          )}

          {/* ID de Pieza */}
          <div className="space-y-2">
            <Label htmlFor="partId" className={touched.partId && errors.partId ? "text-destructive" : ""}>
              ID de Pieza *
            </Label>
            <Input
              id="partId"
              value={formData.partId}
              onChange={(e) =>
                setFormData({ ...formData, partId: e.target.value.toUpperCase() })
              }
              onBlur={() => handleFieldBlur('partId')}
              placeholder="Ej: WINGLET, FUSELAJE, PANEL-01"
              className={touched.partId && errors.partId ? "border-destructive" : ""}
              required
            />
            {touched.partId && errors.partId && (
              <p className="text-xs text-destructive">{errors.partId}</p>
            )}
          </div>

          {/* Operario */}
          <div className="space-y-2">
            <Label>Operario (opcional)</Label>
            <Select
              value={formData.operatorId || "none"}
              onValueChange={(value) =>
                setFormData({ ...formData, operatorId: value === "none" ? "" : value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Asignar operario (opcional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar</SelectItem>
                {operators?.filter((operator: any) => operator.id).map((operator: any) => (
                  <SelectItem key={operator.id} value={operator.id}>
                    {operator.full_name}
                    {operator.employee_id && ` (${operator.employee_id})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* NFC */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Nfc className="w-4 h-4" />
              Etiqueta NFC (opcional)
              {nfcBridge.isReaderReady && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
              )}
            </Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Select
                  value={formData.nfcTagUid || "none"}
                  onValueChange={(value) => {
                    setFormData({ ...formData, nfcTagUid: value === "none" ? "" : value });
                    if (value !== detectedNewTag) setDetectedNewTag(null);
                  }}
                >
                  <SelectTrigger className={detectedNewTag ? "border-green-500 ring-1 ring-green-500" : ""}>
                    <SelectValue placeholder={nfcBridge.isReaderReady ? "Acerca chip o seleccionar..." : "Asignar etiqueta NFC (opcional)"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {detectedNewTag && (
                      <SelectItem value={detectedNewTag} className="text-green-600 font-medium">
                        🆕 Nuevo: {detectedNewTag}
                      </SelectItem>
                    )}
                    {availableTags?.filter((tag: any) => tag.tag_uid).map((tag: any) => (
                      <SelectItem key={tag.tag_uid} value={tag.tag_uid}>
                        {tag.tag_label ? `${tag.tag_label} (${tag.tag_uid})` : tag.tag_uid}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isMobileNfcSupported && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={scanningMobile || isMobileScanning}
                  onClick={async () => {
                    setScanningMobile(true);
                    const result = await scanTagUid();
                    setScanningMobile(false);
                    if (result?.serialNumber) {
                      const uid = result.serialNumber;
                      const exists = (availableTags || []).some((t: any) => t.tag_uid === uid);
                      if (exists) {
                        setFormData((prev) => ({ ...prev, nfcTagUid: uid }));
                        setDetectedNewTag(null);
                        toast.success(`Etiqueta NFC detectada: ${uid}`);
                      } else {
                        setDetectedNewTag(uid);
                        setFormData((prev) => ({ ...prev, nfcTagUid: uid }));
                        toast.success(`Nuevo chip NFC: ${uid} - Se registrará al guardar`);
                      }
                    }
                  }}
                  title="Escanear con NFC del móvil"
                >
                  <Nfc className={`w-4 h-4 ${scanningMobile ? "animate-pulse" : ""}`} />
                </Button>
              )}
            </div>
            {detectedNewTag && formData.nfcTagUid === detectedNewTag && (
              <p className="text-xs text-green-600">
                Chip nuevo detectado. Se registrará automáticamente al guardar.
              </p>
            )}
          </div>

          <Separator className="my-4" />

          {/* Workflow Section */}
          <div className="space-y-4">
            <Label className="flex items-center gap-2 text-base font-semibold">
              <Route className="w-4 h-4" />
              Flujo de Trabajo
            </Label>


            {/* Tipo de Orden de Trabajo */}
            <div className="space-y-2">
              <Label>Tipo de Orden *</Label>
              <Select
                value={formData.orderType}
                onValueChange={(value: "plotter" | "normal" | "design_cnc" | "mold") => {
                  // Si es OT Plotter, usar workflow plotter_only (solo requiere corte)
                  const newWorkflow = value === "plotter" ? "plotter_only" : formData.workflowType;
                  setFormData({ ...formData, orderType: value, workflowType: newWorkflow as any });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="plotter">
                    <div className="flex items-center gap-2">
                      <Scissors className="w-4 h-4 text-cyan-500" />
                      OT Plotter
                    </div>
                  </SelectItem>
                  <SelectItem value="normal">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-500" />
                      OT Normal
                    </div>
                  </SelectItem>
                  <SelectItem value="design_cnc">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500" />
                      OT Diseño/CNC
                    </div>
                  </SelectItem>
                  <SelectItem value="mold">
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-orange-500" />
                      OT Molde
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {formData.orderType === "plotter" && "Orden para trabajos de corte plotter"}
                {formData.orderType === "normal" && "Orden de producción estándar"}
                {formData.orderType === "design_cnc" && "Orden para diseño y mecanizado CNC"}
                {formData.orderType === "mold" && "Orden para fabricación de moldes"}
              </p>
            </div>

            {/* Workflow Type (Proceso Inicial) */}
            <div className="space-y-2">
              <Label>Proceso Inicial *</Label>
              <Select
                value={formData.workflowType}
                onValueChange={(value: "plotter_first" | "direct_lamination" | "standard" | "internal") => {
                  setFormData({
                    ...formData,
                    workflowType: value,
                    linkedPlotterCutId: value === "direct_lamination" ? formData.linkedPlotterCutId : "",
                    moldId: value === "direct_lamination" ? formData.moldId : "",
                    internalTaskType: value === "internal" ? formData.internalTaskType : "",
                  });
                  if (value !== "direct_lamination") {
                    setSelectedFreezerPackage(null);
                    setSelectedMold(null);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="plotter_first">
                    <div className="flex items-center gap-2">
                      <Scissors className="w-4 h-4" />
                      Corte Plotter (crear paquete nuevo)
                    </div>
                  </SelectItem>
                  <SelectItem value="direct_lamination">
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Laminación Directa (paquete existente)
                    </div>
                  </SelectItem>
                  <SelectItem value="standard">
                    <div className="flex items-center gap-2">
                      <Route className="w-4 h-4" />
                      Flujo Estándar
                    </div>
                  </SelectItem>
                  <SelectItem value="internal">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Tarea Interna (TA)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {formData.workflowType === "plotter_first" && "Solo corte: la OT termina al empaquetar y congelar. El paquete queda en el pool del congelador para una futura OT de laminado."}
                {formData.workflowType === "direct_lamination" && "La OT utilizará un paquete ya cortado del congelador (pool) y seguirá el flujo completo"}
                {formData.workflowType === "standard" && "Flujo de trabajo personalizado"}
                {formData.workflowType === "internal" && "Tarea interna: mantenimiento, limpieza, capacitación, etc."}
              </p>
            </div>

            {/* Internal Task Type - Only show for internal workflow */}
            {formData.workflowType === "internal" && (
              <div className="space-y-2">
                <Label>Tipo de Tarea Interna *</Label>
                <Select
                  value={formData.internalTaskType}
                  onValueChange={(value) => setFormData({ ...formData, internalTaskType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona el tipo de tarea" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="training">Capacitación</SelectItem>
                    <SelectItem value="report">Informe</SelectItem>
                    <SelectItem value="audit">Auditoría</SelectItem>
                    <SelectItem value="meeting">Reunión</SelectItem>
                    <SelectItem value="documentation">Documentación</SelectItem>
                    <SelectItem value="cleaning">Limpieza</SelectItem>
                    <SelectItem value="maintenance">Mantenimiento</SelectItem>
                    <SelectItem value="organization">Organización</SelectItem>
                    <SelectItem value="inventory">Inventario</SelectItem>
                    <SelectItem value="equipment_check">Revisión de Equipos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Freezer Package Search - Only show for direct_lamination */}
            {formData.workflowType === "direct_lamination" && (
              <FreezerPackageSearch
                selectedPackageId={formData.linkedPlotterCutId}
                customerId={formData.customerId}
                onSelect={(pkg) => {
                  setSelectedFreezerPackage(pkg);
                  setFormData({
                    ...formData,
                    linkedPlotterCutId: pkg?.id || "",
                  });
                }}
              />
            )}

            {/* Mold Selector - No aplica en OTs de solo corte (plotter): el molde
                se asigna en la OT de laminado que consuma el paquete del congelador */}
            {formData.workflowType !== "internal" && formData.workflowType !== "plotter_first" && formData.workflowType !== "plotter_only" && (
              <div className="space-y-2">
                <MoldSelector
                  value={formData.moldId}
                  onChange={(moldId, mold) => {
                    setSelectedMold(mold);
                    setFormData({
                      ...formData,
                      moldId: moldId,
                    });
                  }}
                  required={formData.workflowType === "direct_lamination"}
                />
                {formData.workflowType !== "direct_lamination" && !formData.moldId && (
                  <p className="text-xs text-muted-foreground">
                    Opcional ahora, pero será requerido antes de iniciar el laminado
                  </p>
                )}
              </div>
            )}
            {(formData.workflowType === "plotter_first" || formData.workflowType === "plotter_only") && (
              <p className="text-xs text-muted-foreground">
                El molde se asignará al crear la OT de laminado con el paquete ya cortado del congelador
              </p>
            )}

            {/* Process Assignments - Hide for internal tasks */}
            {formData.workflowType !== 'internal' && (
              <ProcessAssignments
                workflowType={formData.workflowType}
                assignments={processAssignments}
                onAssignmentsChange={setProcessAssignments}
                workOrderItems={tempWorkOrderItems}
                onValidationChange={(isValid, missingRequired) =>
                  setAssignmentValidation({ isValid, missingRequired })
                }
              />
            )}
          </div>

          <Separator className="my-4" />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Fecha de inicio */}
            <div className="space-y-2">
              <Label htmlFor="startDate" className={touched.startDate && errors.startDate ? "text-destructive" : ""}>
                Fecha de inicio *
              </Label>
              <Input
                id="startDate"
                type="date"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
                className={touched.startDate && errors.startDate ? "border-destructive" : ""}
                required
              />
              <p className="text-xs text-muted-foreground">
                La OT se cargará en producción para esta fecha
              </p>
            </div>

            {/* Cantidad */}
            <div className="space-y-2">
              <Label htmlFor="quantity">Cantidad *</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })
                }
                required
              />
            </div>

            {/* Prioridad */}
            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) =>
                  setFormData({ ...formData, priority: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Baja</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Cargar o Guardar plantilla */}
          <div className="border rounded-lg p-3 bg-muted/30 space-y-2">
            {/* Botón cargar plantilla */}
            {savedTemplates && savedTemplates.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShowTemplateSelector(true)}
              >
                <FileText className="w-4 h-4 mr-2" />
                Cargar Plantilla ({savedTemplates.length})
              </Button>
            )}
            
            {/* Guardar como plantilla */}
            {formData.customerId && (
              <>
              {!showSaveTemplate ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full text-muted-foreground hover:text-foreground"
                  onClick={() => setShowSaveTemplate(true)}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Guardar configuración como plantilla
                </Button>
              ) : (
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    Nombre de la plantilla
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="Ej: Piezas estándar Cliente X"
                      className="text-sm h-8"
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="h-8"
                      onClick={saveAsTemplate}
                    >
                      Guardar
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8"
                      onClick={() => {
                        setShowSaveTemplate(false);
                        setTemplateName('');
                      }}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
              </>
            )}
          </div>
          
          {/* Dialog para seleccionar plantilla */}
          <Dialog open={showTemplateSelector} onOpenChange={setShowTemplateSelector}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Plantillas Guardadas
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {savedTemplates?.map((t: any) => (
                  <Card 
                    key={t.id} 
                    className="p-3 cursor-pointer hover:bg-muted transition-colors" 
                    onClick={() => loadTemplate(t)}
                  >
                    <div className="font-medium">{t.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {t.customer?.company_name || 'Sin cliente'} - {t.project?.name || 'Sin proyecto'}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Usado {t.usage_count || 0} veces
                    </div>
                  </Card>
                ))}
                {(!savedTemplates || savedTemplates.length === 0) && (
                  <p className="text-center text-muted-foreground py-4">
                    No hay plantillas guardadas
                  </p>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Botones */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading || !isFormValid}>
              {loading ? "Creando..." : "Crear Orden"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
